/* eslint-disable */
/**
 * BulkFliesPrice — custom element that manages a "bulk total" price display.
 *
 * Strategy:
 *  1. Liquid pre-renders the initial bulk price (variant.price × 12) so the
 *     page is correct without JS.
 *  2. On `on:variant:change` we swap in the pre-computed bulk price for the
 *     newly selected variant (embedded as JSON in the DOM).
 *  3. On 600ms + 1500ms timeouts we check `BSS_B2B.storage.variantStorage` for
 *     B2B discounted prices and re-render both bulk total and per-unit "each"
 *     price. The same check fires 80ms after every variant change.
 */

/**
 * BulkFliesQty — enforces ±12 stepping on the product-page quantity input.
 *
 * Minmaxify (and other apps) can reset step="12" back to step="1" after load,
 * so we intercept +/- clicks in the capture phase — before any app handler —
 * and apply a hard-coded ±12 delta ourselves, then stop propagation so the
 * default stepUp()/stepDown() doesn't fire a second time.
 */
(function initBulkFliesQty() {
  const BULK_STEP = 12;
  const BULK_MIN  = 12;

  function attachQtyGuard() {
    document.querySelectorAll('quantity-input').forEach(function (wrapper) {
      const input = wrapper.querySelector('input[name="quantity"]');
      if (!input) return;

      // Only patch inputs that were rendered by quantity-input-bulk-flies.
      // They have min="12" in Liquid; Minmaxify may remove it later, so also
      // check for the bulk-flies section ancestor as a fallback.
      const isBulkInput =
        input.getAttribute('min') === String(BULK_MIN) ||
        !!wrapper.closest('[data-section-type="main-product-bulk-flies"]');
      if (!isBulkInput) return;

      function enforceAttrs() {
        input.min  = String(BULK_MIN);
        input.step = String(BULK_STEP);
        if (isNaN(parseInt(input.value, 10)) || parseInt(input.value, 10) < BULK_MIN) {
          input.value = String(BULK_MIN);
        }
      }
      enforceAttrs();

      // Guard against double-attaching on the second setTimeout pass
      if (wrapper._bulkFliesGuarded) return;
      wrapper._bulkFliesGuarded = true;

      // Capture-phase listener runs before Minmaxify / quantity-input.js
      wrapper.addEventListener('click', function (e) {
        const btn = e.target.closest('button[name="plus"], button[name="minus"]');
        if (!btn) return;

        enforceAttrs();

        const current = parseInt(input.value, 10) || BULK_MIN;
        if (btn.name === 'plus') {
          input.value = String(current + BULK_STEP);
        } else {
          input.value = String(Math.max(BULK_MIN, current - BULK_STEP));
        }

        input.dispatchEvent(new Event('change', { bubbles: true }));
        e.stopPropagation();
        e.preventDefault();
      }, true /* capture phase */);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachQtyGuard);
  } else {
    attachQtyGuard();
  }

  // Second pass after apps (Minmaxify etc.) have had time to initialise
  setTimeout(attachQtyGuard, 800);
}());

if (!customElements.get('bulk-flies-price')) {
  class BulkFliesPrice extends HTMLElement {
    connectedCallback() {
      this.qty = parseInt(this.dataset.bulkQty || '12', 10);
      this.currentVariantId = parseInt(this.dataset.currentVariant, 10);
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';

      this.bulkTotalEl = this.querySelector('.price-bulk__total');
      this.bulkValueEl = this.querySelector('.js-bulk-price');
      this.bulkCompareEl = this.querySelector('.js-bulk-compare');
      this.unitPriceEl = this.querySelector('.js-unit-price');

      // Pre-computed bulk prices per variant (rendered by Liquid)
      const dataEl = this.querySelector('.js-bulk-price-data');
      this.bulkData = dataEl ? JSON.parse(dataEl.textContent) : {};

      // Variant change: update bulk total from pre-computed Liquid data
      const variantPicker = document.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener(
          'on:variant:change',
          this.handleVariantChange.bind(this)
        );
      }

      // B2B price check: BSS B2B populates window.BSS_B2B.storage after page
      // load. We run a check at 600ms (covers most cases) and again at 1500ms
      // as a safety net for slow network / late BSS initialisation.
      setTimeout(() => this.handleB2BUpdate(), 600);
      setTimeout(() => this.handleB2BUpdate(), 1500);
    }

    handleVariantChange(e) {
      const variant = e.detail?.variant;
      if (!variant) return;

      this.currentVariantId = variant.id;

      const data = this.bulkData[variant.id];
      if (!data) return;

      // Show Liquid-pre-computed prices immediately (no flicker)
      this.renderBulkPrice(data.price, data.compareAtPrice, data.onSale);
      if (this.unitPriceEl && data.unitPrice) {
        this.unitPriceEl.innerHTML = data.unitPrice;
      }

      // Then let B2B override if a discount applies for this customer
      setTimeout(() => this.handleB2BUpdate(), 80);
    }

    handleB2BUpdate() {
      // Debounce — BSS may fire multiple DOM mutations in one logical update
      clearTimeout(this.b2bTimer);
      this.b2bTimer = setTimeout(() => {
        if (!this.currentVariantId) return;

        const b2bVariant = window.BSS_B2B?.storage?.variantStorage?.get(
          this.currentVariantId
        );
        if (!b2bVariant?.bssB2BPriceAfterCP) return;

        const unitB2B = b2bVariant.bssB2BPriceAfterCP;
        const unitCompare = b2bVariant.compare_at_price ?? b2bVariant.price;
        const bulkB2B = unitB2B * this.qty;
        const bulkCompare = unitCompare * this.qty;
        const onSale = bulkCompare > bulkB2B;

        this.renderBulkPrice(
          this.formatMoney(bulkB2B),
          onSale ? this.formatMoney(bulkCompare) : '',
          onSale
        );

        // Also update the per-unit "each" price with the B2B rate
        if (this.unitPriceEl) {
          this.unitPriceEl.innerHTML = this.formatMoney(unitB2B);
        }
      }, 80);
    }

    renderBulkPrice(price, compareAtPrice, onSale) {
      if (this.bulkValueEl) this.bulkValueEl.innerHTML = price;
      if (this.bulkCompareEl) this.bulkCompareEl.innerHTML = compareAtPrice || '';
      if (this.bulkTotalEl) {
        this.bulkTotalEl.classList.toggle('price--on-sale', !!onSale);
      }
    }

    /**
     * Minimal Shopify money formatter.
     * Replaces any {{amount*}} placeholder in shop.money_format with the
     * formatted decimal string.  Covers CAD (${{amount}} CAD) and most other
     * Shopify currency formats.
     */
    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return this.moneyFormat.replace(/\{\{[^}]+\}\}/g, amount);
    }
  }

  customElements.define('bulk-flies-price', BulkFliesPrice);
}
